import React from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Home CTA band — clean rewrite of legacy molecules/HomeCta (Strapi
// sections.new-home-cta). Rounded band with an optional bg image (dark overlay
// for legible white text), title on the left, description + two CTAs on the
// right. The legacy secondary button opened a Salesforce modal; here it's a
// plain link (the form section owns lead capture). No styled-system/next.
export const homeCtaSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  bgImageUrl: z.string().optional(),
  // `calendly` = open the tenant's Calendly directly instead of following href.
  primaryCta: z.object({ label: z.string(), href: safeUrl, calendly: z.boolean().default(false) }).optional(),
  secondaryCta: z.object({ label: z.string(), href: safeUrl, calendly: z.boolean().default(false) }).optional(),
});

type HomeCtaProps = z.infer<typeof homeCtaSchema>;

const Band = styled.section<{ $bg?: string }>`
  max-width: 112rem;
  margin: 4rem auto;
  padding: 4rem 1.6rem;
  border-radius: 2.4rem;
  color: #ffffff;
  background: ${({ $bg }) =>
    $bg
      ? `linear-gradient(90deg, rgba(6,48,145,0.88), rgba(6,48,145,0.55)), url(${$bg}) center/cover no-repeat`
      : 'linear-gradient(93deg, #063091 0%, #0a3aa8 100%)'};

  @media (min-width: 64em) {
    padding: 7.2rem 8rem;
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 1.6rem;
  align-items: center;

  @media (min-width: 64em) {
    grid-template-columns: 1.4fr 1fr;
    gap: 4rem;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: clamp(2.6rem, 4vw, 5.4rem);
  line-height: 1.1;
  text-align: center;

  @media (min-width: 64em) {
    text-align: left;
  }
`;

const Desc = styled.p`
  margin: 0;
  font-family: 'DM Sans', sans-serif;
  font-size: clamp(1.6rem, 2vw, 2rem);
  text-align: center;

  @media (min-width: 64em) {
    text-align: left;
  }
`;

const Ctas = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.2rem;
  margin-top: 2.4rem;
  justify-content: center;

  @media (min-width: 64em) {
    justify-content: flex-start;
  }
`;

const Cta = styled.a<{ $variant: 'primary' | 'secondary' }>`
  display: inline-block;
  padding: 1.2rem 2.4rem;
  border-radius: 1.6rem;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  ${({ $variant }) =>
    $variant === 'primary'
      ? 'background: #ffffff; color: #063091; border: 1px solid #ffffff;'
      : 'background: transparent; color: #ffffff; border: 1px solid rgba(255,255,255,0.7);'}
`;

function HomeCta({ title, description, bgImageUrl, primaryCta, secondaryCta }: HomeCtaProps) {
  return (
    <Band $bg={bgImageUrl}>
      <Grid>
        <Title>{title}</Title>
        <div>
          {description ? <Desc>{description}</Desc> : null}
          {primaryCta || secondaryCta ? (
            <Ctas>
              {primaryCta ? (
                <Cta $variant="primary" href={primaryCta.calendly ? '#calendly' : primaryCta.href}
                  {...(primaryCta.calendly ? {} : { target: '_blank', rel: 'noreferrer' })}>
                  {primaryCta.label}
                </Cta>
              ) : null}
              {secondaryCta ? (
                <Cta $variant="secondary" href={secondaryCta.calendly ? '#calendly' : secondaryCta.href}>
                  {secondaryCta.label}
                </Cta>
              ) : null}
            </Ctas>
          ) : null}
        </div>
      </Grid>
    </Band>
  );
}

export const homeCtaBlock: BlockDefinition<HomeCtaProps> = {
  type: 'homeCta',
  schema: homeCtaSchema,
  Renderer: HomeCta,
};
